import { inject, Injectable } from '@angular/core';
import { EcaSecurityService, EcaSetupConfigurationService } from '@drc-eca/eca-components-lib';
import { SecurityServiceUserResponse } from '@drc-eca/eca-components-lib';
import { map, switchMap, tap, catchError, take } from 'rxjs/operators';
import { Observable, from, of, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { PortalError } from '../../shared/portal-error.model';
import { UserManagementService } from './user-management.service';
import { OktaService } from '../../core/services/okta.service';
import { OktaTenantService } from '../../core/services/okta-tenant.service';

@Injectable({
  providedIn: 'root'
})
export class UserSecurityService extends EcaSecurityService {
  static badPasswordMessage = 'There was a problem updating your password. Do not reuse any of your last five passwords. Do not use your first or last name or your username.';
  static expiredPasswordMessage = 'Your link to reset your password has expired. To receive a new password reset link, click on Send a New Link.';
  private userManagementService = inject(UserManagementService);
  private oktaService = inject(OktaService);
  private oktaTenantService = inject(OktaTenantService);
  private setupConfigService = inject(EcaSetupConfigurationService);

  constructor() {
    super();
  }

  private isUumUserAbsent(error: any): boolean {
    return error?.status === 401 && (error?.error === 'Unauthorized' || error?.error?.message === 'Unauthorized');
  }

  private isUumUserNotFound(error: any): boolean {
    return error?.status === 404 && error?.error === 'Not Found. 404';
  }

  private getPasswordClientCode(): Observable<string | undefined> {
    return this.appContextService.getSelectedProductCode().pipe(
      map(clientCode => {
        const normalizedCode = clientCode?.trim();
        return normalizedCode && normalizedCode.toUpperCase() !== 'DRCPORTAL' ? normalizedCode : undefined;
      })
    );
  }

  updateUserEca(userProfileObj: any): Observable<any> {
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const url = `eca-security-service/${apiConfig}/v0/user/${userProfileObj.userid}`;
    const reqOptions = { includeToken: true, cache: { cache: false, clearKey: this.getUserCacheKey(userProfileObj.userid) } };
    return this.apiGatewayService.put<SecurityServiceUserResponse>(url, userProfileObj, reqOptions).pipe(
      tap(user => this.sendPermissionChangeAlert(user.userId).subscribe()),
      switchMap(() => this.resetToken()));
  }

  updateUser(userProfileObj: any): Observable<SecurityServiceUserResponse> {
    if (userProfileObj.password && userProfileObj.currentPassword) {
      return this.getPasswordClientCode().pipe(
        take(1),
        switchMap(clientCode => this.userManagementService.changePassword(userProfileObj.userid, userProfileObj.currentPassword, userProfileObj.password, undefined, clientCode)),
        switchMap(() => this.resetToken()),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        tap(user => this.sendPermissionChangeAlert(userProfileObj.userid).subscribe()),
        catchError((error) => {
          // The UUM authorizer uses this generic 401 only when the user is absent from UUM.
          if (this.isUumUserAbsent(error)) {
            return this.updateUserEca(userProfileObj);
          }
          return throwError(error);
        })
      );
    }

    return this.updateUserEca(userProfileObj);
  }

  updateUserWithOktaFallback(userProfileObj: any): Observable<any> {
    if (!userProfileObj.password || !userProfileObj.currentPassword) {
      return this.updateUser(userProfileObj);
    }

    return this.getPasswordClientCode().pipe(
      take(1),
      switchMap(clientCode => this.useOktaPasswordChanges().pipe(
        switchMap(enabled => {
          if (!enabled) {
            return this.updateUser(userProfileObj);
          }
          return this.oktaTenantService.getTenantOrigin(clientCode).pipe(
            switchMap(tenantOrigin => from(this.oktaService.changePassword(tenantOrigin, userProfileObj.currentPassword, userProfileObj.password))),
            switchMap(() => this.resetToken()),
            tap(() => this.sendPermissionChangeAlert(userProfileObj.userid).subscribe()),
            catchError(error => {
              const errorCode = error?.errorCode || error?.error?.errorCode;
              if (error instanceof TypeError || error?.status === 0 ||
                  error?.status === 401 || error?.status === 404 ||
                  ['E0000005', 'E0000007', 'E0000011'].includes(errorCode)) {
                return this.updateUser(userProfileObj);
              }
              return throwError(() => error);
            })
          );
        })
      ))
    );
  }

  private useOktaPasswordChanges(): Observable<boolean> {
    return this.setupConfigService.getAttributeForClient('DRCPORTAL', 'use-okta-password-changes').pipe(
      map(attribute => attribute?.attribute?.defaultValue !== 'false'),
      catchError(() => of(true))
    );
  }

  validatePasswordWithOktaFallback(userId: string, password: string, token?: string, clientCode?: string, userData?: any): Observable<any> {
    if (token) {
      return this.userManagementService.validatePassword(userId, password, token, clientCode);
    }

    return this.appContextService.getSelectedProductCode().pipe(
      take(1),
      switchMap(selectedCode => this.useOktaPasswordChanges().pipe(
        switchMap(enabled => enabled
          ? this.oktaTenantService.getTenantOrigin(selectedCode).pipe(
            switchMap(tenantOrigin => from(this.oktaService.getPasswordComplexityRequirements(tenantOrigin))),
            map(policy => this.validateAgainstOktaPolicy(password, policy, userData))
          )
          : this.userManagementService.validatePassword(userId, password, token, clientCode)
        )
      )),
      catchError(() => this.userManagementService.validatePassword(userId, password, token, clientCode))
    );
  }

  private validateAgainstOktaPolicy(password: string, policy: any, userData?: any): any {
    const complexity = policy?.complexity || policy;
    if (!complexity || !Object.keys(complexity).length) {
      throw new Error('Okta password policy is empty');
    }

    const rules = [
      { name: 'length', count: complexity.minLength ?? complexity.min_length, actual: password.length, rule: 'Minimum length is' },
      { name: 'lowercase', count: complexity.minLowerCase ?? complexity.min_lower_case, actual: (password.match(/[a-z]/g) || []).length, rule: 'Must have at least' },
      { name: 'uppercase', count: complexity.minUpperCase ?? complexity.min_upper_case, actual: (password.match(/[A-Z]/g) || []).length, rule: 'Must have at least' },
      { name: 'number', count: complexity.minNumber ?? complexity.min_number, actual: (password.match(/[0-9]/g) || []).length, rule: 'Must have at least' },
      { name: 'symbol', count: complexity.minSymbol ?? complexity.min_symbol, actual: (password.match(/[^A-Za-z0-9]/g) || []).length, rule: 'Must have at least' }
    ];
    const checks = rules.filter(item => item.count > 0).map(item => ({
      name: item.name,
      valid: item.actual >= item.count,
      rule: item.name === 'length' ? `${item.rule} ${item.count} characters` : `${item.rule} ${item.count} ${item.name} character(s)`
    }));
    if (!checks.length) {
      throw new Error('Okta password policy has no complexity rules');
    }
    const normalizedPassword = password.toLowerCase();
    const firstName = userData?.firstName || userData?.given_name;
    const lastName = userData?.lastName || userData?.family_name;
    const nameValues = [firstName, lastName].filter((value): value is string => typeof value === 'string' && value.length >= 3);
    if (nameValues.length) {
      checks.push({ name: 'excludeName', valid: nameValues.every(value => !normalizedPassword.includes(value.toLowerCase())), rule: 'Must not contain your first or last name' });
    }
    const email = userData?.email || userData?.username;
    if (typeof email === 'string' && email.includes('@')) {
      const chunks = email.toLowerCase().split(/[@.\-_]+/).filter(chunk => chunk.length >= 3);
      checks.push({ name: 'excludeEmail', valid: chunks.every(chunk => !normalizedPassword.includes(chunk)), rule: 'Must not contain your email or parts of it' });
    } else if (policy?.excludeUsername && typeof userData?.username === 'string') {
      checks.push({ name: 'excludeUsername', valid: !normalizedPassword.includes(userData.username.toLowerCase()), rule: 'Must not contain your username' });
    }
    const excludedAttributes = policy?.excludeAttributes || policy?.exclude_attributes;
    if (Array.isArray(excludedAttributes)) {
      for (const entry of excludedAttributes) {
        const attribute = typeof entry === 'string' ? entry : entry?.name || entry?.key;
        if (typeof attribute !== 'string' || /^(first_?name|last_?name|given_?name|family_?name)$/i.test(attribute)) {
          continue;
        }
        const normalizedKey = attribute.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        const value = Object.entries(userData || {}).find(([key]) => key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() === normalizedKey)?.[1];
        if (typeof value === 'string' && value.length >= 3) {
          checks.push({ name: `exclude${attribute}`, valid: !normalizedPassword.includes(value.toLowerCase()), rule: `Must not contain your ${attribute}` });
        }
      }
    }
    return { status: checks.every(check => check.valid) ? 'OK' : 'INVALID', checks };
  }

  sendPermissionChangeAlert(userId: string): Observable<any> {
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const url = `eca-security-service/${apiConfig}/v0/email/permissionChange/${userId}`;
    const reqOptions = { includeToken: true, cache: { cache: false } };
    return this.apiGatewayService.post<any>(url, null, reqOptions).pipe(
      map(response => response));
  }

  // need to reset the token, so that the header updates
  // but want to pass user through as a response
  private resetToken() {
    return this.appContextService.getToken().pipe(
      tap(token => this.appContextService.setToken(token))
    );
  }

  activateAccountEca(newCredentialsObj: any): Observable<any> {
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const url = `eca-security-service/${apiConfig}/v0/password/reset`;
    const reqOptions = { includeToken: false, cache: { cache: false } };
    return this.apiGatewayService.put<any>(url, newCredentialsObj, reqOptions).pipe(
      map(response => response),
      catchError(err => this.handleHttpError(err, UserSecurityService.badPasswordMessage)));
  }

  activateAccount(userId: string, newCredentialsObj: any): Observable<any> {
    return this.userManagementService.resetPassword(userId, newCredentialsObj.password, newCredentialsObj.token, newCredentialsObj.clientCode).pipe(
      map(response => response),
      catchError((error) => {
        if (this.isUumUserNotFound(error)) {
          return this.activateAccountEca(newCredentialsObj);
        }
        return this.handleHttpError(error, UserSecurityService.badPasswordMessage);
      }));
  }

  updateWithNewPasswordEca(newCredentialsObj: any): Observable<any> {
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const url = `eca-security-service/${apiConfig}/v0/password/reset`;
    const reqOptions = { includeToken: false, cache: { cache: false } };
    return this.apiGatewayService.put<any>(url, newCredentialsObj, reqOptions).pipe(
      catchError(err => this.handleHttpError(err, UserSecurityService.badPasswordMessage)));
  }

  updateWithNewPassword(userId: string, newCredentialsObj: any): Observable<any> {
    return this.userManagementService.resetPassword(userId, newCredentialsObj.password, newCredentialsObj.token, newCredentialsObj.clientCode).pipe(
      catchError((error) => {
        if (this.isUumUserNotFound(error)) {
          return this.updateWithNewPasswordEca(newCredentialsObj);
        } else if (error.status === 403) {
          return this.handleHttpError(error, UserSecurityService.expiredPasswordMessage);
        }
        return this.handleHttpError(error, UserSecurityService.badPasswordMessage);
      }));
  }

  sendActivateAccountEmail(clientCode: string, token: string) {
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const url = `eca-security-service/${apiConfig}/v0/email/newUserRefresh/${token}`;
    return this.apiGatewayService.post(url, { clientCode: clientCode });
  }

  private handleHttpError(err: HttpErrorResponse, custMessage: string): Observable<PortalError> {
    const dataError = new PortalError();
    dataError.errorMessage = err.statusText;
    dataError.friendlyErrorMessage = custMessage;
    dataError.status = err.status;
    return throwError(dataError);
  }
}
